// All bundle copies share this page's questions. The timer reads a bridge rather
// than importing another bundle's state; remote pages observe the existing frames.
type Question = { encounterId: string; controller: string; until: number; expires: number; local: boolean };
type ConsentWait = { questions: Map<string, Question>; answered: Set<string>; ticker?: ReturnType<typeof setInterval> };
declare global {
  interface Window {
    __ftConsentWait?: ConsentWait;
    ftHasPendingTriggerConsent?: (encounterId: string) => boolean;
    ftTrackTriggerConsentQuestion?: (askId: string, encounterId: string, controller: string, until: number) => () => void;
  }
}
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();

if (typeof window !== 'undefined' && !window.__ftConsentWait) {
  const state: ConsentWait = window.__ftConsentWait = { questions: new Map(), answered: new Set() };
  const active = (): string => upper((window as Window & { ftGetActiveEncounterId?: () => unknown }).ftGetActiveEncounterId?.());
  window.ftHasPendingTriggerConsent = id => [...state.questions.values()].some(q => q.encounterId === upper(id) && q.expires > Date.now());

  const render = (): void => {
    const local = [...state.questions.values()].filter(q => q.local && q.encounterId === active());
    let banner = document.getElementById('ft-trigger-consent-wait');
    if (!local.length) { banner?.remove(); return; }
    if (!banner && document.body) {
      banner = document.createElement('div');
      banner.id = 'ft-trigger-consent-wait';
      banner.setAttribute('role', 'status');
      banner.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);z-index:1090;'
        + 'padding:10px 16px;border-radius:8px;background:#1d1a24;color:#f2eee6;border:1px solid #8a6cff;pointer-events:none';
      document.body.appendChild(banner);
    }
    const seconds = Math.max(0, Math.ceil((Math.max(...local.map(q => q.until)) - Date.now()) / 1000));
    const text = `Waiting on reactions (${local.length}) — ${seconds}s`;
    if (banner && banner.textContent !== text) banner.textContent = text;
  };
  const changed = (): void => {
    render();
    window.dispatchEvent(new CustomEvent('ft:trigger-consent-wait-change'));
    if (!state.questions.size && state.ticker !== undefined) { clearInterval(state.ticker); state.ticker = undefined; }
  };
  const finish = (askId: string): void => {
    state.answered.add(askId);
    if (state.answered.size > 512) state.answered.delete(state.answered.values().next().value!);
    if (state.questions.delete(askId)) changed();
  };
  const track = (askId: string, encounterId: string, controller: string, until: number, local: boolean): void => {
    if (state.answered.has(askId)) return;
    const previous = state.questions.get(askId);
    if (previous) { previous.local ||= local; return; } // a sender's socket echo joins its local question
    state.questions.set(askId, { encounterId: upper(encounterId), controller: upper(controller), until, expires: until + 1500, local });
    changed();
    state.ticker ??= setInterval(() => {
      for (const [id, question] of state.questions) if (question.expires <= Date.now()) finish(id);
      render();
    }, 250);
  };
  window.ftTrackTriggerConsentQuestion = (askId, encounterId, controller, until) => {
    track(askId, encounterId, controller, until, true);
    return () => finish(askId);
  };
  window.addEventListener('ft:janus-trigger-prompt', (event: Event) => {
    const frame = (event as CustomEvent<Record<string, unknown>>).detail;
    if (!frame || !frame.ask_id || !frame.encounter_id) return;
    const askId = upper(frame.ask_id);
    if (frame.phase === 'answer') {
      const question = state.questions.get(askId);
      if (question && question.encounterId === upper(frame.encounter_id) && question.controller === upper(frame.controller_user_id)) finish(askId);
    } else if (frame.phase === 'ask' && frame.kind !== 'offer') {
      const windowMs = Math.max(0, Math.min(5000, Number(frame.window_ms ?? 5000)));
      if (Number.isFinite(windowMs)) track(askId, upper(frame.encounter_id), upper(frame.controller_user_id), Date.now() + windowMs, false);
    }
  });
  // Switching encounters hides the old actor's banner; the bounded questions still
  // settle normally and cannot pause the new encounter's timer.
  for (const name of ['ft:encounter-installed', 'ft:encounter-state-received']) window.addEventListener(name, render);
}
export {};
